import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import FullTemplateDomain from "entities/domain/templates/full_template";
import WhatsappTemplate from "entities/domain/whatsapp-template";
import { WritableDraft } from "immer";

export interface CreateTemplatePayload {
  title: string;
  text: string;
  channels: string[];
  favourite: boolean;
  shortcut: string | null;
  subject: string | null;
  category: string | null;
  file?: File;
  mediaUrl?: string | null;
  mediaType?: string | null;
}

export enum TemplateFilter {
  WHATSAPP = "whatsapp",
  REGULAR = "regular",
  EMAIL = "email",
}

export interface EditTemplatePayload {
  id: string;
  title: string;
  text: string;
  channels: string[];
  favourite: boolean;
  shortcut: string | null;
  subject: string | null;
  category: string | null;
  file?: File;
  mediaType?: string | null;
  mediaUrl?: string | null;
}

export interface SyncTemplatePayload {
  id: string;
}

export interface DeleteTemplatePayload {
  id: string;
}

export interface AbstractTemplate {
  getWholeText: () => string;
  getMedia: () => { type: string; url: string } | null;
  canEdit: () => boolean;
  isGeneric: () => boolean;
  isCustom: () => boolean;
  getWhatsAppStatusColor: () => string;
  isWhatsappTemplate: () => boolean;
  isRegularTemplate: () => boolean;
  getCustomFieldKeys: () => string[];
}

export type RegularOrWhatsappTemplate = FullTemplateDomain | WhatsappTemplate;

interface TemplatesState {
  // UI state only
  modalLoading: boolean;
  activeTemplateFilter: TemplateFilter | undefined;
  showPendingDeletion: boolean;
  toastMessage: {
    new: boolean;
    success: string;
    errors: string[];
  };
}
const initialState: TemplatesState = {
  modalLoading: false,
  activeTemplateFilter: undefined,
  showPendingDeletion: false,
  toastMessage: {
    new: false,
    success: "",
    errors: [],
  },
};

export const appendOrUpdateTemplate = <T extends RegularOrWhatsappTemplate>(
  template: T | WritableDraft<T>,
  templates: WritableDraft<T>[]
): T[] => {
  const exists = templates.some((t) => t.id === template.id);

  if (exists) {
    return templates.map((t) => (template.id === t.id ? template : t)) as T[];
  }

  return [template, ...templates] as T[];
};

const templateSlice = createSlice({
  name: "templates",
  initialState,
  reducers: {
    setactiveTemplateFilter: (state, action: PayloadAction<TemplateFilter>) => {
      state.activeTemplateFilter = action.payload;
    },
    createTemplate: (state) => {
      state.modalLoading = true;
    },
    createTemplateSuccess: (state) => {
      state.modalLoading = false;
      state.toastMessage = {
        success: "Template created successfully!",
        errors: [],
        new: true,
      };
    },
    createTemplateFail: (state, action: PayloadAction<any[]>) => {
      state.modalLoading = false;
      state.toastMessage = {
        success: "",
        errors: action.payload,
        new: true,
      };
    },
    editTemplate: (state) => {
      state.modalLoading = true;
    },
    editTemplateSuccess: (state) => {
      state.modalLoading = false;
      state.toastMessage = {
        success: "Template updated successfully!",
        errors: [],
        new: true,
      };
    },
    editTemplateFail: (state, action: PayloadAction<any[]>) => {
      state.modalLoading = false;
      state.toastMessage = {
        success: "",
        errors: action.payload,
        new: true,
      };
    },
    deleteTemplateSuccess: (state) => {
      state.toastMessage = {
        success: "Template deleted successfully!",
        errors: [],
        new: true,
      };
    },
    deleteTemplateFail: (state, action: PayloadAction<any[]>) => {
      state.toastMessage = {
        success: "",
        errors: action.payload,
        new: true,
      };
    },
    resetStore(state) {
      state.modalLoading = false;
      state.activeTemplateFilter = undefined;
      state.showPendingDeletion = false;
      state.toastMessage = {
        new: false,
        success: "",
        errors: [],
      };
    },
    setShowPendingDeletion: (state, action: PayloadAction<boolean>) => {
      state.showPendingDeletion = action.payload;
    },
  },
});

export const {
  createTemplate,
  createTemplateSuccess,
  createTemplateFail,
  editTemplate,
  editTemplateSuccess,
  editTemplateFail,
  deleteTemplateSuccess,
  deleteTemplateFail,
  setactiveTemplateFilter,
  resetStore,
  setShowPendingDeletion,
} = templateSlice.actions;
export const templateSliceActions = templateSlice.actions;
export default templateSlice.reducer;
